import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  HStack,
  Input,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react';
import { TitleBar } from '@resistapp/client/components/bars/title-bar';
import { theme } from '@resistapp/client/components/shared/theme';
import { MaybeApiError } from '@resistapp/client/components/states/error';
import { Loading } from '@resistapp/client/components/states/loading';
import { useUser } from '@resistapp/client/contexts/use-user-context';
import { useSearchParamsContext } from '@resistapp/client/contexts/search-params-context';
import { useAdminShipments, useCancelShipment, usePatchAdminShipment, useShipments } from '@resistapp/client/hooks/api';
import { useToastWithoutDebounce } from '@resistapp/client/hooks/use-resistomap-toast';
import { isResistomapAdminUser } from '@resistapp/client/utils/access';
import { copyTextToClipboard } from '@resistapp/client/utils/clipboard';
import { formatWorkbookTimestamp } from '@resistapp/client/utils/lab-workbook';
import type { AdminShipmentResponse } from '@resistapp/common/api-types';
import { ShipmentStatus } from '@resistapp/common/types';
import { useQueryClient } from '@tanstack/react-query';
import { chain, compact } from 'lodash';
import { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { ShipmentDocumentCustomerModal } from '../shipment-view/shipment-document-centre';

function getStatusColor(status: ShipmentStatus): string {
  if (status === ShipmentStatus.RECEIVED) {
    return 'green';
  }
  if (status === ShipmentStatus.CANCELLED) {
    return 'red';
  }
  if (status === ShipmentStatus.PROCESSED) {
    return 'teal';
  }
  if (status === ShipmentStatus.PICKED_UP) {
    return 'purple';
  }
  if (status === ShipmentStatus.SCHEDULED) {
    return 'blue';
  }
  return 'orange';
}

function formatShipmentAddress(shipment: AdminShipmentResponse): string {
  return (
    compact([
      shipment.pickupAddressLine1,
      shipment.pickupAddressLine2,
      shipment.pickupAddressLine3,
      compact([shipment.pickupPostalCode, shipment.pickupCity]).join(' '),
      shipment.pickupCountry,
    ]).join('\n') || 'Not set'
  );
}

function formatPackageDetails(shipment: AdminShipmentResponse): string {
  const weight = shipment.packageWeightKg == null ? null : `${shipment.packageWeightKg} kg`;
  const dimensions =
    shipment.packageLengthCm == null || shipment.packageWidthCm == null || shipment.packageHeightCm == null
      ? null
      : `${shipment.packageLengthCm} x ${shipment.packageWidthCm} x ${shipment.packageHeightCm} cm`;

  return compact([weight, dimensions]).join(', ') || 'Not set';
}

function buildShipmentClipboardText(shipment: AdminShipmentResponse): string {
  const contents =
    shipment.items.map(item => `- ${item.description} x ${item.count}`).join('\n') || '- No items listed';
  return compact([
    `Shipment #${shipment.id}`,
    `Status: ${shipment.status}`,
    `Organisation: ${shipment.organisation.name} (${shipment.organisation.id})`,
    shipment.labWorkbook
      ? `Lab workbook: ${shipment.labWorkbook.name ?? 'Untitled workbook'} (${shipment.labWorkbook.id})`
      : null,
    `Pickup date: ${shipment.pickupDate}`,
    `Pickup window: ${shipment.pickupWindowStart} - ${shipment.pickupWindowEnd}`,
    `Pickup address:\n${formatShipmentAddress(shipment)}`,
    `Contact: ${shipment.contactName}`,
    `Contact email: ${shipment.contactEmail}`,
    shipment.contactTelephoneNumber ? `Contact phone: ${shipment.contactTelephoneNumber}` : null,
    shipment.taxId ? `Tax ID: ${shipment.taxId}` : null,
    `Package: ${formatPackageDetails(shipment)}`,
    'Contents:',
    contents,
  ]).join('\n');
}

function getShipmentQuotaState(shipment: AdminShipmentResponse): { label: string; color: string } {
  if (!shipment.labWorkbook) {
    return { label: 'Not quota-linked', color: 'gray.600' };
  }
  if (shipment.quotaSpentAt) {
    return { label: 'Quota spent', color: 'green.700' };
  }
  if (shipment.status === ShipmentStatus.CANCELLED) {
    return { label: 'Quota released', color: 'gray.600' };
  }
  return { label: 'Quota reserved', color: 'gray.600' };
}

export function AdminShipmentsView() {
  const { user } = useUser();
  const isResistomapAdmin = isResistomapAdminUser(user);

  return (
    <div style={{ marginBottom: theme.spacing[16] }}>
      <TitleBar
        title="Shipments"
        $extraWide={true}
        controls={
          isResistomapAdmin ? (
            <Button as={RouterLink} to="/admin/shipment">
              New shipment
            </Button>
          ) : undefined
        }
      />
      <VStack align="stretch" spacing={4} padding={theme.spacing[4]}>
        <AdminShipmentsSection scope="admin" showHeader={false} />
      </VStack>
    </div>
  );
}

type ShipmentSectionScope = 'admin' | 'user';

export function AdminShipmentsSection({
  scope,
  showHeader = true,
}: {
  scope: ShipmentSectionScope;
  showHeader?: boolean;
}) {
  const [includeReceivedOverride, setIncludeReceivedOverride] = useState<boolean | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [cancellingShipmentId, setCancellingShipmentId] = useState<number | null>(null);
  const { searchParams: routeSearchParams, setSearchParamsStable: setRouteSearchParams } = useSearchParamsContext();
  const { user, userLoading } = useUser();
  const isResistomapAdmin = isResistomapAdminUser(user);
  const isAdminScope = scope === 'admin';
  const canModifyShipments = isAdminScope && isResistomapAdmin;
  const canViewShipments = isAdminScope ? isResistomapAdmin : Boolean(user);
  const includeReceived = includeReceivedOverride ?? !isAdminScope;

  const showToast = useToastWithoutDebounce();
  const queryClient = useQueryClient();
  const invalidateShipmentsQueries = async () => {
    await queryClient.invalidateQueries({
      predicate: query => typeof query.queryKey[0] === 'string' && query.queryKey[0].startsWith('/admin/shipments'),
    });
  };

  const params = useMemo(() => {
    const value = new URLSearchParams();
    if (includeReceived) {
      value.set('includeReceived', '1');
    }
    if (search.trim().length > 0) {
      value.set('search', search.trim());
    }
    return value;
  }, [includeReceived, search]);

  const adminShipmentsQuery = useAdminShipments(params, {
    enabled: !userLoading && isAdminScope && isResistomapAdmin,
  });
  const userShipmentsQuery = useShipments(params, {
    enabled: !userLoading && !isAdminScope && Boolean(user),
  });
  const { data, isPending, error } = isAdminScope ? adminShipmentsQuery : userShipmentsQuery;
  const patchShipment = usePatchAdminShipment();
  const cancelShipment = useCancelShipment();

  if (userLoading) {
    return <Loading />;
  }

  if (!canViewShipments) {
    return null;
  }

  const rows = data?.rows ?? [];
  const selectedShipmentId = Number(routeSearchParams.get('shipment'));
  const selectedShipment =
    Number.isInteger(selectedShipmentId) && selectedShipmentId > 0
      ? rows.find(shipment => shipment.id === selectedShipmentId) ?? null
      : null;

  const openDocumentCentre = (shipmentId: number) => {
    const nextSearchParams = new URLSearchParams(routeSearchParams);
    nextSearchParams.set('shipment', String(shipmentId));
    setRouteSearchParams(nextSearchParams, { replace: true });
  };

  const closeDocumentCentre = () => {
    const nextSearchParams = new URLSearchParams(routeSearchParams);
    nextSearchParams.delete('shipment');
    setRouteSearchParams(nextSearchParams, { replace: true });
  };

  const handleStatusChange = (shipment: AdminShipmentResponse, status: ShipmentStatus) => {
    if (status === shipment.status) {
      return;
    }
    void (async () => {
      try {
        await patchShipment.mutateAsync({
          shipmentId: shipment.id,
          body: { status },
        });
        await invalidateShipmentsQueries();
        await queryClient.invalidateQueries({
          queryKey: [`/admin/shipment/${shipment.id}`],
        });
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['/shipping/workbooks'] }),
          queryClient.invalidateQueries({
            queryKey: ['organisationLabWorkbooks', shipment.organisation.id],
          }),
        ]);
        showToast({
          title: 'Shipment status updated',
          description: `Shipment #${shipment.id} is now ${status}.`,
          status: 'success',
          duration: 5000,
        });
      } catch (statusError) {
        showToast({
          title: 'Failed to update status',
          description: statusError instanceof Error ? statusError.message : 'Please try again later.',
          status: 'error',
          duration: 7000,
        });
      }
    })();
  };

  const handleCopyShipment = (shipment: AdminShipmentResponse) => {
    void (async () => {
      try {
        await copyTextToClipboard(buildShipmentClipboardText(shipment));
        showToast({
          title: 'Shipment copied',
          description: `Shipment #${shipment.id} is ready to paste.`,
          status: 'success',
          duration: 4000,
        });
      } catch (copyError) {
        showToast({
          title: 'Failed to copy shipment',
          description: copyError instanceof Error ? copyError.message : 'Please try again later.',
          status: 'error',
          duration: 7000,
        });
      }
    })();
  };

  const handleCancelShipment = (shipment: AdminShipmentResponse) => {
    if (
      isAdminScope ||
      shipment.createdBy !== user?.id ||
      shipment.status !== ShipmentStatus.REQUESTED ||
      shipment.quotaSpentAt !== null ||
      cancellingShipmentId !== null
    ) {
      return;
    }

    const confirmed = window.confirm(
      `Cancel shipment request #${shipment.id}? Its reserved shipping quota will become available again.`,
    );
    if (!confirmed) {
      return;
    }

    void (async () => {
      setCancellingShipmentId(shipment.id);
      try {
        await cancelShipment.mutateAsync({ shipmentId: shipment.id });
        await Promise.all([
          queryClient.invalidateQueries({
            predicate: query => typeof query.queryKey[0] === 'string' && query.queryKey[0].startsWith('/shipments'),
          }),
          queryClient.invalidateQueries({ queryKey: ['/shipping/workbooks'] }),
          queryClient.invalidateQueries({
            queryKey: ['organisationLabWorkbooks', shipment.organisation.id],
          }),
        ]);
        showToast({
          title: 'Shipment request cancelled',
          description: `Shipment #${shipment.id} was cancelled and its quota was released.`,
          status: 'success',
          duration: 5000,
        });
      } catch (cancelError) {
        showToast({
          title: 'Failed to cancel shipment request',
          description: cancelError instanceof Error ? cancelError.message : 'Please try again later.',
          status: 'error',
          duration: 7000,
        });
      } finally {
        setCancellingShipmentId(null);
      }
    })();
  };

  return (
    <VStack align="stretch" spacing={4}>
      {showHeader ? (
        <Flex align="center" justify="space-between" gap={theme.spacing[3]} wrap="wrap">
          <Text fontSize="lg" fontWeight={600}>
            Shipment requests
          </Text>
          {canModifyShipments ? (
            <Button as={RouterLink} to="/admin/shipment">
              New shipment
            </Button>
          ) : null}
        </Flex>
      ) : null}

      <Box bg="white" borderRadius={6} padding={theme.spacing[3]} boxShadow="sm">
        <Flex direction={{ base: 'column', md: 'row' }} justify="space-between" gap={theme.spacing[3]}>
          <HStack flex={1}>
            <Input
              placeholder="Search by id, workbook, organisation, contact"
              value={searchInput}
              onChange={event => {
                setSearchInput(event.target.value);
              }}
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  setSearch(searchInput);
                }
              }}
            />
            <Button
              onClick={() => {
                setSearch(searchInput);
              }}
            >
              Search
            </Button>
          </HStack>
          <HStack spacing={theme.spacing[4]}>
            <Checkbox
              isChecked={includeReceived}
              onChange={event => {
                setIncludeReceivedOverride(event.currentTarget.checked);
              }}
              whiteSpace="nowrap"
            >
              Show received
            </Checkbox>
          </HStack>
        </Flex>
      </Box>

      <Box bg="white" borderRadius={6} padding={theme.spacing[3]} boxShadow="sm" overflowX="auto">
        {error ? (
          <MaybeApiError error={error} />
        ) : isPending ? (
          <Loading />
        ) : rows.length === 0 ? (
          <Text color="gray.600">No shipments match your filters.</Text>
        ) : (
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Pickup</Th>
                <Th>Contents</Th>
                <Th>Organisation</Th>
                <Th>Lab workbook</Th>
                <Th>Contact</Th>
                <Th>Status</Th>
                <Th>Updated</Th>
                {!isAdminScope ? <Th>Actions</Th> : null}
              </Tr>
            </Thead>
            <Tbody>
              {rows.map(shipment => {
                const quotaState = getShipmentQuotaState(shipment);
                return (
                  <Tr key={shipment.id}>
                    <Td>
                      <ShipmentPickupCell shipment={shipment} canOpen={canModifyShipments} />
                    </Td>
                    <Td>
                      <ShipmentContentsCell
                        shipment={shipment}
                        canCopy={canModifyShipments}
                        onCopy={handleCopyShipment}
                      />
                    </Td>
                    <Td>
                      {canModifyShipments ? (
                        <Link
                          as={RouterLink}
                          to={`/admin/organisation/${shipment.organisation.id}`}
                          color={theme.colors.blue800}
                        >
                          {shipment.organisation.name}
                        </Link>
                      ) : (
                        <Text>{shipment.organisation.name}</Text>
                      )}
                    </Td>
                    <Td>
                      {shipment.labWorkbook ? (
                        <VStack align="flex-start" spacing={0.5}>
                          <Link as={RouterLink} to={`/lab/${shipment.labWorkbook.id}`} color={theme.colors.blue800}>
                            {shipment.labWorkbook.name ?? 'Untitled workbook'}
                          </Link>
                          <Text fontSize="xs" color={quotaState.color}>
                            {quotaState.label}
                          </Text>
                        </VStack>
                      ) : (
                        <Text fontSize="sm" color="gray.500">
                          No lab workbook
                        </Text>
                      )}
                    </Td>
                    <Td>
                      <VStack align="flex-start" spacing={0.5}>
                        <Text>{shipment.contactName}</Text>
                        <Text fontSize="xs" color="gray.600">
                          {shipment.contactEmail}
                        </Text>
                        {shipment.contactTelephoneNumber ? (
                          <Text fontSize="xs" color="gray.600">
                            {shipment.contactTelephoneNumber}
                          </Text>
                        ) : null}
                      </VStack>
                    </Td>
                    <Td>
                      <ShipmentStatusCell
                        shipment={shipment}
                        canModify={canModifyShipments}
                        onStatusChange={handleStatusChange}
                      />
                    </Td>
                    <Td>
                      <Text fontSize="sm" color="gray.600">
                        {formatWorkbookTimestamp(shipment.updatedAt)}
                      </Text>
                    </Td>
                    {!isAdminScope ? (
                      <Td>
                        <VStack align="flex-start" spacing={2}>
                          {shipment.documentCentreVisible ? (
                            <Button
                              size="xs"
                              colorScheme="blue"
                              onClick={() => {
                                openDocumentCentre(shipment.id);
                              }}
                            >
                              View documents
                              {shipment.documents.length > 0 ? ` (${shipment.documents.length})` : ''}
                            </Button>
                          ) : null}
                          {shipment.createdBy === user?.id &&
                          shipment.status === ShipmentStatus.REQUESTED &&
                          shipment.quotaSpentAt === null ? (
                            <Button
                              size="xs"
                              colorScheme="red"
                              variant="outline"
                              isLoading={cancellingShipmentId === shipment.id}
                              isDisabled={cancellingShipmentId !== null && cancellingShipmentId !== shipment.id}
                              onClick={() => {
                                handleCancelShipment(shipment);
                              }}
                            >
                              Cancel request
                            </Button>
                          ) : null}
                          {!shipment.documentCentreVisible &&
                          !(
                            shipment.createdBy === user?.id &&
                            shipment.status === ShipmentStatus.REQUESTED &&
                            shipment.quotaSpentAt === null
                          ) ? (
                            <Text color="gray.500">-</Text>
                          ) : null}
                        </VStack>
                      </Td>
                    ) : null}
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        )}
      </Box>
      <ShipmentDocumentCustomerModal
        shipment={selectedShipment}
        isOpen={Boolean(selectedShipment?.documentCentreVisible)}
        onClose={closeDocumentCentre}
      />
    </VStack>
  );
}

function ShipmentPickupCell({ shipment, canOpen }: { shipment: AdminShipmentResponse; canOpen: boolean }) {
  const content = (
    <VStack align="flex-start" spacing={0.5}>
      <Text>{shipment.pickupDate}</Text>
      <Text fontSize="xs" color="gray.600">
        {shipment.pickupWindowStart} - {shipment.pickupWindowEnd}
      </Text>
      <Text fontSize="xs" color="gray.600">
        {[shipment.pickupAddressLine1, shipment.pickupCity, shipment.pickupCountry].filter(Boolean).join(', ') ||
          'Address not set'}
      </Text>
    </VStack>
  );

  return canOpen ? (
    <Link as={RouterLink} to={`/admin/shipment/${shipment.id}/view`} color={theme.colors.blue800}>
      {content}
    </Link>
  ) : (
    content
  );
}

function ShipmentItemsPreview({ shipment }: { shipment: AdminShipmentResponse }) {
  return (
    <VStack align="flex-start" spacing={0.5}>
      {chain(shipment.items)
        .take(3)
        .map(item => (
          <Text key={item.id} fontSize="xs" color="gray.600">
            {item.description} x {item.count}
          </Text>
        ))
        .value()}
      {shipment.items.length > 3 ? (
        <Text fontSize="xs" color="gray.500">
          +{shipment.items.length - 3} more items
        </Text>
      ) : null}
    </VStack>
  );
}

function ShipmentContentsCell({
  shipment,
  canCopy,
  onCopy,
}: {
  shipment: AdminShipmentResponse;
  canCopy: boolean;
  onCopy: (shipment: AdminShipmentResponse) => void;
}) {
  if (!canCopy) {
    return <ShipmentItemsPreview shipment={shipment} />;
  }

  return (
    <Box
      as="button"
      type="button"
      bg="transparent"
      border="0"
      borderRadius={4}
      cursor="copy"
      padding={1}
      textAlign="left"
      width="100%"
      _hover={{ bg: 'blue.50' }}
      onClick={() => {
        onCopy(shipment);
      }}
    >
      <ShipmentItemsPreview shipment={shipment} />
    </Box>
  );
}

function ShipmentStatusCell({
  shipment,
  canModify,
  onStatusChange,
}: {
  shipment: AdminShipmentResponse;
  canModify: boolean;
  onStatusChange: (shipment: AdminShipmentResponse, status: ShipmentStatus) => void;
}) {
  if (!canModify) {
    return <Badge colorScheme={getStatusColor(shipment.status)}>{shipment.status}</Badge>;
  }

  return (
    <Menu>
      <MenuButton as={Badge} colorScheme={getStatusColor(shipment.status)} cursor="pointer" _hover={{ opacity: 0.8 }}>
        {shipment.status} ▾
      </MenuButton>
      <MenuList minW="auto">
        {Object.values(ShipmentStatus).map(status => (
          <MenuItem
            key={status}
            fontWeight={status === shipment.status ? 600 : 400}
            onClick={() => {
              onStatusChange(shipment, status);
            }}
          >
            <Badge colorScheme={getStatusColor(status)} mr={2}>
              {status}
            </Badge>
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
}
